import React from 'react';
import { Button, Select, Space, type SelectProps } from 'antd';
import { buildStatusSelectionOptions } from '../statusSelection';

interface OrderStatusTransitionSelectProps extends Omit<SelectProps<string>, 'options'> {
  statuses: readonly string[];
  currentStatus: string;
  allowedTransitions: readonly string[];
  getStatusLabel: (status: string) => string;
  currentLabel: string;
  unavailableLabel: string;
  /**
   * Statuses such as ON_HOLD remain visible in the full workflow, but need an
   * intentional action instead of being reachable by arrow-key navigation in
   * the normal progression select.
   */
  guardedStatuses?: readonly string[];
  guardedStatusReason?: string;
  guardedStatusActionLabel?: (status: string) => string;
  guardedStatusHint?: string;
  onGuardedStatusSelect?: (status: string) => void;
}

const OrderStatusTransitionSelect: React.FC<OrderStatusTransitionSelectProps> = ({
  statuses,
  currentStatus,
  allowedTransitions,
  getStatusLabel,
  currentLabel,
  unavailableLabel,
  guardedStatuses = [],
  guardedStatusReason,
  guardedStatusActionLabel,
  guardedStatusHint,
  onGuardedStatusSelect,
  disabled,
  ...selectProps
}) => {
  const guarded = new Set(guardedStatuses);
  const options = buildStatusSelectionOptions(statuses, currentStatus, allowedTransitions);
  const availableGuardedStatuses = options.filter((option) => (
    !option.disabled && guarded.has(option.status)
  ));

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Select
        {...selectProps}
        disabled={disabled}
        options={options.map((option) => {
          const requiresExplicitAction = !option.disabled && guarded.has(option.status);
          const reason = option.reason === 'current'
            ? currentLabel
            : option.reason === 'unavailable'
              ? unavailableLabel
              : requiresExplicitAction
                ? guardedStatusReason
                : null;

          return {
            value: option.status,
            disabled: option.disabled || requiresExplicitAction,
            label: (
              <span>
                {getStatusLabel(option.status)}
                {reason && (
                  <span style={{ marginLeft: 8, color: '#94a3b8', fontSize: 12 }}>
                    — {reason}
                  </span>
                )}
              </span>
            ),
          };
        })}
      />
      {onGuardedStatusSelect && guardedStatusActionLabel && availableGuardedStatuses.map((option) => (
        <div key={option.status}>
          <Button
            type="dashed"
            disabled={disabled}
            onClick={() => onGuardedStatusSelect(option.status)}
          >
            {guardedStatusActionLabel(option.status)}
          </Button>
          {guardedStatusHint && (
            <div style={{ marginTop: 4, color: '#64748b', fontSize: 12 }}>
              {guardedStatusHint}
            </div>
          )}
        </div>
      ))}
    </Space>
  );
};

export default OrderStatusTransitionSelect;
